import React from 'react';
import { Dropdown } from 'react-bootstrap';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import Text from '../../../../shared/_TBComponents/Text';
import { TransitionGlobal } from '../../../../shared/GlobalStyles';
import CustomToggle from './CustomToggle';

const DropDownMenuWrapper = styled('div')`
  width: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 20%;
  color: #333333;
  .dropdown-item {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 5px;
    padding-left: 10px;
    transition: none;
    text-transform: uppercase;
    svg {
      fill: #999999;
    }
    :hover {
      transition: linear 0s;
      background: #0dcc8a;
      color: #ffffff;
      div p {
        color: #ffffff;
      }
      svg {
        fill: #ffffff;
      }
    }
  }
  .dropdown-menu {
    padding: 0px;
  }
  .dropdown-divider {
    margin: 0px;
  }
  .dropdown-icon {
    transition: ${TransitionGlobal};
    :hover {
      opacity: 0.6;
    }
  }
`;

const dropdownTextStyle = {
  fontSize: 14,
  marginLeft: '10px',
  paddingTop: '2px',
  fontWeight: 900,
  textTransform: 'uppercase',
  letterSpacing: 1.3,
  color: '#999999',
};

const DropdownItem = ({
  icon, text, onClick, iconSize = 12, ariaLabel,
}) => (
  <Dropdown.Item onClick={onClick} aria-label={ariaLabel}>
    <IcomoonReact iconSet={iconSet} size={iconSize} icon={icon} />
    <Text
      className='drop-icon-text'
      styledTextProps={dropdownTextStyle}
    >
      {text}
    </Text>
  </Dropdown.Item>
);

DropdownItem.propTypes = {
  icon: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  iconSize: PropTypes.number,
  ariaLabel: PropTypes.string,
};

DropdownItem.defaultProps = {
  iconSize: 12,
  ariaLabel: null,
};

const FeedDropdownOptions = ({
  contentObject, editFunction, editText, deleteFunction, reportFunction, showReport,
}) => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const currentUserIsAuthor = contentObject.userId === currentUser.id;
  const isAdmin = currentUser.admin;

  const dropdownOptionPermissions = {
    showEdit: contentObject.editAllowed && currentUserIsAuthor,
    showDelete: contentObject.deleteAllowed && (currentUserIsAuthor || isAdmin),
    showReport: showReport && !currentUserIsAuthor,
    showDivider: currentUserIsAuthor,
  };

  const getMenuItems = () => {
    const items = [];

    if (dropdownOptionPermissions.showEdit) {
      items.push({
        type: 'item',
        icon: 'pencil',
        text: editText,
        onClick: editFunction,
        iconSize: 12,
        ariaLabel: 'Edit',
      });
    }

    if (dropdownOptionPermissions.showDivider) {
      items.push({ type: 'divider' });
    }

    if (dropdownOptionPermissions.showDelete) {
      items.push({
        type: 'item',
        icon: 'trashcan',
        text: 'Delete',
        onClick: deleteFunction,
        iconSize: 15,
        ariaLabel: 'Delete',
      });
    }

    if (dropdownOptionPermissions.showReport) {
      items.push({
        type: 'item',
        icon: 'warning',
        text: 'Report Post',
        onClick: reportFunction,
        iconSize: 15,
        ariaLabel: 'Report',
      });
    }

    return items;
  };

  const renderMenuItem = (item, index) => {
    if (item.type === 'divider') {
      return <Dropdown.Divider key={index} />;
    }

    return (
      <DropdownItem
        key={index}
        icon={item.icon}
        text={item.text}
        onClick={item.onClick}
        iconSize={item.iconSize}
        ariaLabel={item.ariaLabel}
      />
    );
  };

  return (
    <DropDownMenuWrapper>
      <Dropdown bsPrefix='trick-fix' alignRight>
        <Dropdown.Toggle as={CustomToggle} />
        <Dropdown.Menu>
          {getMenuItems().map(renderMenuItem)}
        </Dropdown.Menu>
      </Dropdown>
    </DropDownMenuWrapper>
  );
};

export default FeedDropdownOptions;

FeedDropdownOptions.propTypes = {
  contentObject: PropTypes.instanceOf(Object).isRequired,
  editFunction: PropTypes.func.isRequired,
  editText: PropTypes.string.isRequired,
  deleteFunction: PropTypes.func.isRequired,
  reportFunction: PropTypes.func.isRequired,
  showReport: PropTypes.bool,
};

FeedDropdownOptions.defaultProps = {
  showReport: true,
};
